import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Factory, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// This page consumes `#session_id=...` returned by Emergent OAuth and exchanges it
// for our own JWT via POST /api/auth/google/session. Users are NEVER auto-created:
// backend enforces "email must already exist in db.users and be active".
export default function AuthCallbackPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setError("Retorno do Google inválido: session_id ausente.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        await loginWithToken(data.token, data.user);
        // Limpa o fragment da URL para evitar reprocessamento em refreshes
        window.history.replaceState({}, document.title, "/dashboard");
        toast.success(`Bem-vindo, ${data.user?.name || data.user?.email}`);
        navigate("/dashboard", { replace: true });
      } catch (ex) {
        const msg = formatApiError(ex.response?.data?.detail) || "Falha ao autenticar via Google.";
        setError(msg);
      }
    })();
  }, [location.hash, loginWithToken, navigate]);

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="w-full max-w-md rounded-lg border bg-white p-8 shadow-sm" data-testid="auth-callback-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-[#234935]">
            <Factory className="h-5 w-5 text-[#C5A059]" />
          </div>
          <div>
            <div className="font-display text-[17px] font-semibold">TheoKratos <span className="text-[#8A6B2B]">RH</span></div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">Autenticação Google</div>
          </div>
        </div>

        {!error ? (
          <div className="flex items-center gap-3 text-[13.5px] text-muted-foreground" data-testid="auth-callback-loading">
            <Loader2 className="h-4 w-4 animate-spin text-[#234935]" />
            <span>Validando sua sessão do Google…</span>
          </div>
        ) : (
          <div data-testid="auth-callback-error">
            <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-3 text-[13px] text-red-700">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
            <div className="mt-5 text-[13px] text-muted-foreground">
              Verifique se o seu e-mail corporativo já foi cadastrado pelo administrador.
            </div>
            <Link
              to="/login"
              className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-[#234935] px-4 text-[13px] font-medium text-white hover:bg-[#2c5c43]"
              data-testid="auth-callback-back-btn"
            >
              Voltar ao login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
